import { ClientAction, ClientMessageType, ServerMessage, SyncMessageType } from "../../../shared/messageTypes";
import { registerBugReportServerWorker } from "../bugReport/bugReporter";
import { SteamLobby } from "./steamLobby";
import { SteamDataChannel } from "./steamNetworking";
import { STEAM_VIRTUAL_PORT } from "./steamPeerConnection";
import { WebRTCConnectionPoller } from "./webrtcPeerConnection";

type Interceptor = (message: ServerMessage[]) => ServerMessage[];

/**Handles communication with the server, abstracting out nature of that communication */
export interface ConnectionToServer {
	send(msg: Object): void;
	addListener(onServerMessage: (event: ServerMessage[]) => void): void;
	addInterceptListener(onIntercept: Interceptor): void;
	addCloseListener(onClose: (intentional: boolean) => void): void;
	reconnect(): Promise<void>;
	sendConnectMessage(): void;
	close(): void;
	isSecondPlayer: boolean;
}

export class WSClient implements ConnectionToServer {
	private wsClient: WebSocket;
	private address: string;
	private messageListeners: Array<(message: ServerMessage[]) => void> = [];
	private closeListeners: Array<(intentional: boolean) => void> = [];
	// Sockets we closed ourselves. Tracked per socket rather than with a single flag, because
	// reconnect() swaps in a new socket while the old one's (async) close event may still be coming.
	private intentionallyClosed = new WeakSet<WebSocket>();
	private intercepters: Array<Interceptor> = [];
	private pendingMessages: ClientMessageType[] = [];
	isSecondPlayer: boolean = false;

	constructor(address: string) {
		this.address = address;
		this.wsClient = this.createSocket();
	}

	private createSocket(): WebSocket {
		const ws = new WebSocket(this.address);
		ws.addEventListener('open', () => {
			const pending = this.pendingMessages;
			this.pendingMessages = [];
			for (const msg of pending) {
				this.send(msg);
			}
		});
		ws.addEventListener('message', (event: MessageEvent) => {
			let message = JSON.parse(event.data);
			for (const intercepter of this.intercepters) {
				message = intercepter(message);
			}
			this.messageListeners.forEach(l => l(message));
		});
		ws.addEventListener('close', () => {
			const intentional = this.intentionallyClosed.has(ws);
			this.closeListeners.forEach(l => l(intentional));
		});
		return ws;
	}

	async reconnect() {
		return await new Promise<void>((resolve, reject) => {
			this.wsClient = this.createSocket();
			const openHandler = () => {
				this.wsClient.removeEventListener('error', errorHandler);
				resolve();
			};
			const errorHandler = () => {
				this.wsClient.removeEventListener('open', openHandler);
				reject();
			};
			this.wsClient.addEventListener('open', openHandler, { once: true });
			this.wsClient.addEventListener('error', errorHandler, { once: true });
		});
	}

	send(msg: ClientMessageType) {
		if (this.wsClient.readyState === WebSocket.OPEN) {
			this.wsClient.send(JSON.stringify(msg));
		} else {
			this.pendingMessages.push(msg);
		}
	}

	addListener(onServerMessage: (message: ServerMessage[]) => void) {
		this.messageListeners.push(onServerMessage);
	}

	addCloseListener(onClose: (intentional: boolean) => void) {
		this.closeListeners.push(onClose);
	}

	addInterceptListener(onIntercept: Interceptor) {
		this.intercepters.push(onIntercept);
	}

	sendConnectMessage() {
		this.send({ messageType: ClientAction.ClientHello });
	}

	close() {
		this.intentionallyClosed.add(this.wsClient);
		this.wsClient.close();
	}
}

export class WebWorkerClient implements ConnectionToServer {
	isSecondPlayer: boolean = false;
	private static instance: WebWorkerClient | null = null;
	static getInstance() {
		if (this.instance === null) {
			this.instance = new WebWorkerClient();
		}
		return this.instance;
	}
	private worker: Worker;
	private intercepters: Array<Interceptor> = [];
	private closeListeners: Array<(intentional: boolean) => void> = [];
	private workerListeners: Array<(event: MessageEvent) => void> = [];
	private constructor() {
		this.worker = new Worker('./bundle2.js');
		//Let the bug reporter pull the server's logs out of the worker on demand
		registerBugReportServerWorker(this.worker);
	}

	send(msg: ClientMessageType) {
		this.worker.postMessage(msg);
	}

	addListener(onServerMessage: (message: ServerMessage[]) => void) {
		const handler = (event: MessageEvent) => {
			let message = event.data;
			for (const intercepter of this.intercepters) {
				message = intercepter(message);
			}
			onServerMessage(message);
		};
		this.workerListeners.push(handler);
		this.worker.addEventListener('message', handler);
	}

	addCloseListener(onClose: (intentional: boolean) => void) {
		this.closeListeners.push(onClose);
	}

	async reconnect() {
	}

	addInterceptListener(onIntercept: Interceptor) {
		this.intercepters.push(onIntercept);
	}

	sendConnectMessage() {
		this.send({ messageType: ClientAction.ClientHello });
	}

	close() {
		this.send({ messageType: ClientAction.HostResetGame })

		//The worker outlives the game, so its wiring has to go with the game that made it.
		for (const handler of this.workerListeners) {
			this.worker.removeEventListener('message', handler);
		}
		this.workerListeners = [];
		this.intercepters = [];

		const listeners = this.closeListeners;
		this.closeListeners = [];
		listeners.forEach(l => l(true));
	}
}

export class WebRTCClient implements ConnectionToServer {
	private messageListeners: Array<(message: ServerMessage[]) => void> = [];
	private closeListeners: Array<(intentional: boolean) => void> = [];
	isSecondPlayer: boolean = true;

	constructor(private peer: RTCPeerConnection, private dataChannel: RTCDataChannel, private lobbyId: string) {
		this.peer = peer;
		this.dataChannel = dataChannel;
		this.lobbyId = lobbyId;
		this.setupChannelHandlers(peer, dataChannel);
	}

	// Single routing handler per channel — all listeners are fired atomically, then peer.close() is called.
	// The old per-listener pattern had each handler call peer.close(), which mutated connectionState to
	// 'closed' before later handlers ran, so they skipped. Moving peer.close() to AFTER the forEach
	// fixes that. The connection must still be closed here so that if the OS fires 'disconnected' then
	// 'failed' in sequence, the handler only runs once (peer.close() prevents 'failed' from following).
	private setupChannelHandlers(peer: RTCPeerConnection, dataChannel: RTCDataChannel) {
		dataChannel.addEventListener('message', (event: MessageEvent) => {
			const message = JSON.parse(event.data);
			this.messageListeners.forEach(l => l(message));
		});

		//Reported once, from whichever signal arrives first
		let reportedDropped = false;
		const onDropped = (reason: string) => {
			if (reportedDropped) return;
			reportedDropped = true;
			console.log("WebRTC connection dropped: " + reason);
			this.closeListeners.forEach(l => l(false));
			peer.close();
		};

		dataChannel.addEventListener('close', () => onDropped('data channel closed'));
		dataChannel.addEventListener('error', () => onDropped('data channel error'));

		peer.addEventListener('connectionstatechange', () => {
			if (peer.connectionState === 'disconnected' || peer.connectionState === 'failed') {
				onDropped('peer connection ' + peer.connectionState);
			}
		});
	}

	copyConnection(source: WebRTCClient) {
		this.peer = source.peer;
		this.dataChannel = source.dataChannel;
		this.lobbyId = source.lobbyId;
		// source's constructor already attached handlers routing to source's empty arrays — harmless.
		// Add our own handlers routing to this.messageListeners / this.closeListeners.
		this.setupChannelHandlers(this.peer, this.dataChannel);
	}

	send(msg: ServerMessage) {
		console.log("WebRTC client sending server message");
		if (msg.messageType !== SyncMessageType.PassThrough) {
			this.dataChannel.send(JSON.stringify({ messageType: ClientAction.PassThrough, payload: msg }));
		}
		else {
			this.dataChannel.send(JSON.stringify(msg.payload));
		}
	}

	addListener(onServerMessage: (message: ServerMessage[]) => void) {
		this.messageListeners.push(onServerMessage);
	}

	addCloseListener(onClose: (intentional: boolean) => void): void {
		this.closeListeners.push(onClose);
	}

	sendConnectMessage() {
		console.log("WebRTC client sending connect message");
		this.dataChannel.send(JSON.stringify({ messageType: ClientAction.ClientHello, isPassThrough: true }));
	}

	async reconnect() {
		const inProgressJoiningConnection = new WebRTCConnectionPoller();
		try {
			const reconnected = await inProgressJoiningConnection.joinFromBrowser(this.lobbyId, this);
			if (!reconnected) {
				throw new Error("WebRTCClient: reconnect did not produce a connection");
			}
			this.sendConnectMessage();
		}
		finally {
			inProgressJoiningConnection.dispose();
		}
	}

	addInterceptListener(_onIntercept: Interceptor): void {
		//Webrtc clients never use this
	}

	close() {
		if (this.peer.connectionState === 'closed') return;
		this.peer.close();
		this.closeListeners.forEach(l => l(true));
	}
}

export class SteamClient implements ConnectionToServer {
	hostId: string;
	isSecondPlayer: boolean = true;
	lobby: SteamLobby | null;

	constructor(public channel: SteamDataChannel, hostId: string, lobby: SteamLobby | null = null) {
		this.hostId = hostId;
		this.lobby = lobby;
	}

	send(msg: ServerMessage): void {
		if (msg.messageType !== SyncMessageType.PassThrough) {
			this.channel.send(JSON.stringify({ messageType: ClientAction.PassThrough, payload: msg }));
		}
		else {
			this.channel.send(JSON.stringify(msg.payload));
		}
	}

	addListener(onServerMessage: (message: ServerMessage[]) => void): void {
		this.channel.addEventListener('message', (event: { data: string }) => {
			const message = JSON.parse(event.data);
			onServerMessage(message);
		});
	}

	addCloseListener(onClose: (intentional: boolean) => void): void {
		this.channel.addEventListener('close', (intentional: boolean) => {
			onClose(intentional);
		});
	}

	sendConnectMessage(): void {
		console.log("Steam client sending connect message");
		this.channel.send(JSON.stringify({ messageType: ClientAction.ClientHello, isPassThrough: true }));
	}

	async reconnect() {
		const success = await window.electronApi?.connectToPeer(this.hostId, STEAM_VIRTUAL_PORT);
		if (!success) {
			throw new Error("Failed to initiate Steam connection");
		}
		await this.channel.connect();
		this.sendConnectMessage();
	}

	getDataChannel(): SteamDataChannel {
		return this.channel;
	}

	addInterceptListener(_onIntercept: Interceptor): void {
		// Steam clients never use this (same as WebRTC clients)
	}

	close(): void {
		if (this.lobby) {
			this.lobby.leave();
		}
		this.channel.close();
	}
}